import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { ChevronRight, MoreHorizontal, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words sm:gap-2.5',
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  )
}

function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean
}) {
  const Comp = asChild ? Slot : 'a'
  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn('hover:text-foreground transition-colors', className)}
      {...props}
    />
  )
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('text-foreground font-normal', className)}
      {...props}
    />
  )
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  )
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  )
}

/**
 * AppBreadcrumb — the redesigned header breadcrumb: an accent icon chip + a
 * mono host eyebrow + a serif "App / Page" title. Presentational only (no
 * hooks/events) so it stays server-renderable and adds no weight to the
 * marketing pages that import the primitives above.
 *
 * The codebase ships HSL-triplet design tokens, while this design uses bare
 * --accent / --muted-fg / --font-serif color vars — so we bridge them into
 * scoped --afn-* custom properties (hsl()-wrapped) right on the root element.
 */
const AFN_BC_TOKENS: React.CSSProperties = {
  // @ts-expect-error — CSS custom properties
  '--afn-accent': 'hsl(var(--accent))',
  '--afn-accent-fg': 'hsl(var(--accent-foreground))',
  '--afn-muted-fg': 'hsl(var(--muted-foreground))',
  '--afn-fg': 'hsl(var(--foreground))',
  '--afn-font-mono': 'var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace)',
  '--afn-font-serif': 'var(--font-source-serif, ui-serif, Georgia, "Times New Roman", serif)',
}

function AppBreadcrumb({
  host,
  appName,
  pageTitle,
  icon: _icon,
  className,
  style,
  ...props
}: React.ComponentProps<'div'> & {
  host?: string
  appName?: string
  pageTitle?: string
  /** @deprecated icon chip removed from the breadcrumb design; prop kept for API compat. */
  icon?: LucideIcon
}) {
  return (
    <div
      data-slot="app-breadcrumb"
      className={className}
      style={{ display: 'flex', alignItems: 'center', minWidth: 0, ...AFN_BC_TOKENS, ...style }}
      {...props}
    >
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.18, minWidth: 0, flex: '1 1 0%' }}>
        <span style={{ font: '700 8.5px var(--afn-font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--afn-muted-fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {host}
        </span>
        <span style={{ font: '600 15px var(--afn-font-serif)', color: 'var(--afn-fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {appName}
          {pageTitle ? (
            <>
              {' '}
              <span style={{ color: 'var(--afn-muted-fg)', fontWeight: 400 }}>/</span>{' '}
              <span style={{ fontStyle: 'italic' }}>{pageTitle}</span>
            </>
          ) : null}
        </span>
      </div>
    </div>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  AppBreadcrumb,
}
